import type { ReactNode } from 'react';

type Tone = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const toneClasses: Record<Tone, { value: string; bg: string }> = {
  default: { value: 'text-gray-900', bg: 'bg-white' },
  primary: { value: 'text-primary-600', bg: 'bg-primary-50' },
  success: { value: 'text-green-700', bg: 'bg-green-50' },
  warning: { value: 'text-amber-700', bg: 'bg-amber-50' },
  danger: { value: 'text-red-700', bg: 'bg-red-50' },
  info: { value: 'text-blue-700', bg: 'bg-blue-50' },
};

interface StatCardProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  /** 컴팩트 사이즈 (페이지 헤더 우측 등) */
  compact?: boolean;
}

export function StatCard({
  label,
  value,
  sub,
  tone = 'default',
  compact = false,
}: StatCardProps) {
  const t = toneClasses[tone];
  return (
    <div
      className={`rounded-lg border border-gray-200 ${t.bg} ${
        compact ? 'px-3 py-2' : 'px-4 py-3'
      }`}
    >
      <div
        className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
      >
        {label}
      </div>
      <div
        className={`mt-0.5 font-bold ${t.value} ${
          compact ? 'text-base' : 'text-xl'
        }`}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 text-xs text-gray-500">{sub}</div>
      )}
    </div>
  );
}

interface StatGridProps {
  children: ReactNode;
  /** 한 줄에 표시할 카드 수 (기본 자동) */
  cols?: 2 | 3 | 4 | 5;
}

/**
 * 통계 카드 그리드 (반응형)
 */
export function StatGrid({ children, cols = 4 }: StatGridProps) {
  const colsClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  }[cols];
  return <div className={`grid gap-2 ${colsClass}`}>{children}</div>;
}
